// tabHistory — client-side helpers for the "each tab is a history
// entry" behavior + Backspace-triggers-back navigation.
//
// Two exports:
//
//   isEditableTarget(target)
//     True when `target` is an element the user is actively editing
//     (an <input>, <textarea>, contenteditable node, code editor,
//     ProseMirror, TipTap, or any element nested inside one). Used
//     by the backspace handler to detect "the user is typing" and
//     LET backspace pass through as a real delete keystroke instead
//     of hijacking it as navigation.
//
//   installBackspaceBackHandler({ document = window.document } = {})
//     Attaches a keydown listener at document root. When the user
//     presses Backspace OUTSIDE any editable target, prevents the
//     default (browsers don't navigate on backspace anymore but
//     defense-in-depth) and calls window.history.back().
//     Returns an unsubscribe function.
//
// Design notes:
//   - We do NOT hijack Backspace on Mac either — some Mac users have
//     mapped Cmd+Left as "back" but backspace-back is a Windows/Linux
//     browser convention (Chrome removed it in 2016 due to the
//     "backspace in a form field" data loss bug we're specifically
//     avoiding here). Explicit reactivation via this handler puts
//     the same convention back for our CRM but with the editable-
//     target guard the browsers didn't have.
//   - The "you're currently typing" guard is deliberately broad —
//     any editor state, any nested rich-text framework, any
//     contenteditable descendant counts. False positives (backspace
//     doing nothing) are always safer than false negatives
//     (backspace losing form data mid-note).

// Element tags that always mean "the user is typing text."
const ALWAYS_EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

// Input types where Backspace is a delete keystroke, not a control.
// Excludes 'button', 'submit', 'checkbox', 'radio', 'file', etc. —
// those don't process Backspace as text editing.
const TYPING_INPUT_TYPES = new Set([
  'text', 'password', 'email', 'search', 'tel', 'url', 'number',
  'date', 'datetime-local', 'month', 'time', 'week',
  // Fallback: an <input> with no type attribute defaults to 'text'.
  '', undefined, null,
]);

/**
 * Return true when the target is inside an editable region.
 *
 * Checks (in order):
 *   1. Direct tag match (INPUT / TEXTAREA / SELECT)
 *   2. contenteditable="true" on the target or any ancestor
 *   3. role="textbox" / role="combobox" on target or ancestor
 *   4. .ProseMirror / .tiptap / .cm-editor / .monaco-editor class
 *      anywhere in the ancestor chain (rich-text + code editors)
 *   5. data-editable="true" (project-specific escape hatch)
 */
export function isEditableTarget(target) {
  if (!target || typeof target !== 'object') return false;

  // Text-input elements. Guard on input type since <input type=button>
  // etc. don't accept text — a Backspace on those is a navigation
  // intent, not a delete keystroke.
  const tag = target.tagName;
  if (ALWAYS_EDITABLE_TAGS.has(tag)) {
    if (tag !== 'INPUT') return true;
    // No type attribute (or explicitly empty) defaults to 'text'.
    const rawType = target.type;
    const t = rawType == null ? '' : String(rawType).toLowerCase();
    return TYPING_INPUT_TYPES.has(t);
  }

  // Walk up the ancestor chain looking for editable markers.
  let node = target;
  const MAX_DEPTH = 32;
  for (let depth = 0; depth < MAX_DEPTH && node; depth++) {
    // contenteditable="true" (or contenteditable="") is editable.
    const ce = typeof node.getAttribute === 'function' ? node.getAttribute('contenteditable') : null;
    if (ce === '' || ce === 'true') return true;

    // ARIA textbox / combobox roles.
    const role = typeof node.getAttribute === 'function' ? node.getAttribute('role') : null;
    if (role === 'textbox' || role === 'combobox') return true;

    // Rich-text + code editor markers.
    const cls = typeof node.className === 'string' ? node.className : '';
    if (cls) {
      if (cls.indexOf('ProseMirror') !== -1) return true;
      if (cls.indexOf('tiptap') !== -1)      return true;
      if (cls.indexOf('cm-editor') !== -1)   return true;
      if (cls.indexOf('monaco-editor') !== -1) return true;
    }

    // Explicit escape hatch for CRM-specific rich components that
    // don't fit the patterns above.
    const dataEdit = typeof node.getAttribute === 'function' ? node.getAttribute('data-editable') : null;
    if (dataEdit === 'true') return true;

    node = node.parentNode || node.parentElement;
    if (!node) break;
  }

  return false;
}

/**
 * Install a global Backspace → history.back() handler. Returns an
 * unsubscribe function; call it in the useEffect cleanup.
 *
 * The handler is a no-op when the event target is inside an editable
 * region (see isEditableTarget). Also ignores modified Backspace
 * (Shift/Ctrl/Meta/Alt) — those are almost never navigation intents.
 *
 * Options:
 *   document — override the document to bind to. Defaults to
 *              window.document. Useful for tests + iframe scenarios.
 *   history  — override the history API. Defaults to window.history.
 */
export function installBackspaceBackHandler(options = {}) {
  const doc = options.document || (typeof window !== 'undefined' ? window.document : null);
  const hist = options.history || (typeof window !== 'undefined' ? window.history : null);
  if (!doc || !hist) return () => {};

  const onKeyDown = (e) => {
    if (!e || e.key !== 'Backspace') return;
    // Modified backspace = not a navigation intent.
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
    // Anywhere the user might be typing → hands off.
    if (isEditableTarget(e.target)) return;
    // Not typing → treat as navigation.
    if (typeof e.preventDefault === 'function') e.preventDefault();
    try { hist.back(); } catch { /* jsdom / older browsers — safe no-op */ }
  };

  doc.addEventListener('keydown', onKeyDown);
  return () => doc.removeEventListener('keydown', onKeyDown);
}

/**
 * Popstate scroll-restoration helper. When the user hits Back, the
 * hashchange listener in usePersistentState updates the tab state,
 * but the browser doesn't restore scroll for hash-only navigation.
 * We stored scrollY in the history state on push (see
 * persistentState.writeHash historyMode:'push'), so on popstate we
 * scroll to it. Fires once per popstate.
 *
 * Returns an unsubscribe function.
 */
export function installScrollRestoration(options = {}) {
  const win = options.window || (typeof window !== 'undefined' ? window : null);
  if (!win) return () => {};

  const onPop = (e) => {
    const y = e && e.state && typeof e.state.sfaScrollY === 'number'
      ? e.state.sfaScrollY
      : null;
    if (y == null) return;
    // Defer to next tick so the tab render has a chance to lay out
    // its content — scrolling before content is placed just no-ops.
    if (typeof win.setTimeout === 'function') {
      win.setTimeout(() => { try { win.scrollTo(0, y); } catch { /* noop */ } }, 0);
    } else {
      try { win.scrollTo(0, y); } catch { /* noop */ }
    }
  };
  win.addEventListener('popstate', onPop);
  return () => win.removeEventListener('popstate', onPop);
}

